/**
 * The current site's config (sites/$SITE/site.config.ts) plus small helpers.
 *
 * Relative imports only (besides @site): vite.config.ts bundles this file for
 * the build-time prerender.
 */
import config from '@site/site.config';
import type { LocalizedText, SiteConfig, SiteLanguage } from './types';

export * from './types';

export const SITE: SiteConfig = config;
export default SITE;

export const SITE_ID = SITE.id;
export const SITE_NAME = SITE.brand.name;
/** Canonical origin, e.g. https://japansnowwatch.com */
export const SITE_ORIGIN = SITE.origin;
export const SITE_LANGUAGES: readonly SiteLanguage[] = SITE.languages;
export const DEFAULT_LANGUAGE: SiteLanguage = SITE.languages[0];

export function isSiteLanguage(value: string | undefined | null): value is SiteLanguage {
  return !!value && (SITE.languages as string[]).includes(value);
}

/**
 * A localised value in `lang`, falling back to English (then the first slot
 * present) when the slot is missing or empty. Works for strings, arrays and
 * objects keyed by language.
 */
export function pick<T>(value: Partial<Record<SiteLanguage, T>> | null | undefined, lang: string): T {
  if (!value) return undefined as T;
  const own = (value as Record<string, T>)[lang];
  if (own !== undefined && own !== null && (own as unknown) !== '') return own;
  if (value.en !== undefined && value.en !== null) return value.en as T;
  const first = Object.values(value).find((v) => v !== undefined && v !== null);
  return first as T;
}

/** Shorthand for pick() on LocalizedText. */
export const text = (value: LocalizedText | Partial<Record<SiteLanguage, string>> | null | undefined, lang: string): string =>
  pick(value, lang) ?? '';

export const countryName = (lang: string) => text(SITE.country.name, lang);

/** Absolute URL on this site for a root-relative path. */
export const siteUrl = (pathname = '/') => `${SITE.origin}${pathname.startsWith('/') ? '' : '/'}${pathname}`;

/** /:lang/... path for the current site's best-resorts page. */
export const BEST_RESORTS_PATH = `/${SITE.routes.bestResorts}`;

/** ISO code of the site's own currency (prices in the data are in it). */
export const BASE_CURRENCY = SITE.currency.code;

/** False when the site has no public cameras (SiteConfig.features.liveCameras). */
export const HAS_LIVE_CAMERAS = SITE.features.liveCameras !== false;
/** Resort pages without cameras show the conditions + satellite panel (SiteConfig.features.noCameraPanel). */
export const NO_CAMERA_PANEL = SITE.features.noCameraPanel === true;
export const SNOWMAKING_FORECAST = SITE.features.snowmakingForecast === true;
